import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios';
import { baseUrl } from "../../comman/config";
import { useNavigate, Link } from 'react-router-dom';

// The signed-in user's own details.
//
// Department and Role are shown but not editable -- they decide what the account may do, and
// nobody edits their own permissions from their own profile. That is User Master's job, and it
// has the no-escalation rule on it. The greyed fields on the client's sketch say the same thing.
export default function Profile() {

    const [formData, setFormData] = useState({ name: '', mobile: '', center: '', mht_id: '' });
    const [readOnly, setReadOnly] = useState({ login_id: '', role_name: '', department_names: '' });
    const [validationErrors, setValidationErrors] = useState({ name: false, mobile: false });
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        const getProfile = async () => {
            try {
                const response = await axios.get(`${baseUrl}/user_master/profile?user_id=${userId}`);
                const user = response.data?.data ?? {};
                setFormData({
                    name: user.name || '',
                    mobile: user.mobile || '',
                    center: user.center || '',
                    mht_id: user.mht_id || '',
                });
                setReadOnly({
                    login_id: user.username || '',
                    role_name: user.role_name || '',
                    // No departments means every department, which is what "All" is everywhere else.
                    department_names: user.department_names || 'All',
                });
            } catch (error) {
                console.log(error);
                setMessage({ type: 'error', text: error?.response?.data?.msg ?? 'Could not load your profile.' });
            }
        };
        if (userId) getProfile();
    }, [userId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
        setValidationErrors((previous) => ({ ...previous, [name]: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {
            name: formData.name.trim() === '',
            mobile: formData.mobile.trim() === '',
        };
        setValidationErrors(errors);
        if (Object.values(errors).some(Boolean)) {
            const first = formRef.current.querySelector('.invalid');
            if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setMessage(null);
        setSaving(true);
        try {
            const response = await axios.post(`${baseUrl}/user_master/profile`, {
                user_id: Number(userId),
                formData,
            });
            // The copy the rest of the app shows follows the name, not the login id -- the
            // login id is not this screen's to change.
            localStorage.setItem('name', response.data?.name ?? formData.name);
            setMessage({ type: 'success', text: 'Profile updated.' });
        } catch (error) {
            console.log(error);
            setMessage({ type: 'error', text: error?.response?.data?.msg ?? 'Could not save your profile.' });
        }
        setSaving(false);
    };

    const field = (name, label, required = false) => (
        <div className="sm:col-span-2">
            <label htmlFor={name} className="block text-sm font-medium leading-6 text-gray-900">{label}{required ? ' *' : ''}</label>
            <div className="mt-2">
                <input
                    type="text"
                    name={name}
                    id={name}
                    placeholder={label}
                    value={formData[name]}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border ${validationErrors[name] ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                />
            </div>
            {validationErrors[name] && <div className="text-red-500">Please enter {label.toLowerCase()}.</div>}
        </div>
    );

    const readOnlyField = (label, value, hint) => (
        <div className="sm:col-span-2">
            <label className="block text-sm font-medium leading-6 text-gray-900">{label}</label>
            <div className="mt-2">
                <div className="block w-full rounded-md border border-gray-300 bg-gray-200 py-1.5 px-1.5 text-gray-700 sm:text-sm sm:leading-6">
                    {value || '-'}
                </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">{hint}</div>
        </div>
    );

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Profile</h1>
                <Link to="/change_password">
                    <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Change Password</button>
                </Link>
            </div>
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg px-4 py-2">
                <form ref={formRef}>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        {readOnlyField('Login ID', readOnly.login_id, 'What you sign in with. Only the SuperAdmin can change it.')}
                        {field('name', 'Name', true)}
                        {field('mobile', 'Mobile', true)}
                        {readOnlyField('Department', readOnly.department_names, 'Set by an administrator in User Master.')}
                        {readOnlyField('Role', readOnly.role_name, 'Set by an administrator in User Master.')}
                        {field('center', 'Center')}
                        {field('mht_id', 'MHT ID')}
                    </div>

                    {message && (
                        <div className={`mt-4 ${message.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                            {message.text}
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-end gap-x-6">
                        <button type="button" onClick={() => navigate(-1)} className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                        <button
                            onClick={handleSubmit}
                            disabled={saving}
                            type="submit"
                            className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
